import React, { useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Box, Stepper, Step, CircularProgress, StepButton } from '@mui/material'

import WizardContextProvider from './shared/WizardContext'
import useCompanyConfig from '../hooks/api/useCompanyConfig'
import useBusinessTypes from '../hooks/api/useBusinessTypes'
import useIsAdmin from '../hooks/useIsAdmin'

export const steps = [
  { label: 'Name & Logo', path: '/logo' },
  { label: 'Users', path: '/invite' },
  { label: 'Account profile', path: '/customer-profile' },
  { label: 'Custom fields', path: '/custom-fields' },
  { label: 'Contacts', path: '/contacts' },
  // { label: 'Actions', path: '/actions' },
  { label: 'Products', path: '/products' },
  { label: 'Pipelines', path: '/pipelines' },
  { label: 'Lead Import', path: '/lead-import' },
  { label: 'Magic Button', path: '/magic-button' },
]

const Wizard = () => {
  const { isAdmin } = useIsAdmin()

  const {
    companyConfig,
    updateCompanyConfig,
    isLoading,
    setCompanyConfig,
    patchUpdateCompanyConfig,
  } = useCompanyConfig()

  const [wizardState, setWizardState] = useState({})

  const updateWizardState = (mergeObj) => {
    setWizardState((prev) => ({ ...prev, ...mergeObj }))
  }

  const wizardSteps = companyConfig?.wizardSteps || {}

  const navigate = useNavigate()

  // NICE-TO-HAVE: make a wrapper for admin-only routes
  useEffect(() => {
    if (!isAdmin) return navigate('/')
  }, [isAdmin])

  const businessTypesQuery = useBusinessTypes()

  const firstIncompleteStep = steps.find(
    (s) =>
      wizardSteps[s.path] !== 'COMPLETE' && wizardSteps[s.path] !== 'SKIPPED'
  )

  const firstIncompleteStepIndex = steps.indexOf(firstIncompleteStep)

  const [activeStep, setActiveStep] = useState(0)

  const { pathname } = useLocation()

  const paths = useMemo(() => steps.map((s) => s.path))

  const currentStep = steps[activeStep]

  const navigateToNext = (isSkipping) => {
    const nextStep = steps[activeStep + 1]
    const newVal = {
      ...wizardSteps,
      [currentStep.path]: isSkipping ? 'SKIPPED' : 'COMPLETE',
    }

    patchUpdateCompanyConfig({ wizardSteps: newVal })
      .then(() => {
        if (!nextStep)
          return navigate('/dashboard/todos?from_initial_setup=true')

        const { path } = nextStep
        navigate(`/setup${path}`)
      })
      .catch()
  }

  const navigateToPrevious = () => {
    const step = steps[activeStep - 1]

    if (!step) return

    const { path } = step
    navigate(`/setup${path}`)
  }

  const navigateToFirstIncompleteStep = () => {
    const firstPath = steps[0].path

    if (firstIncompleteStepIndex === -1 && !wizardSteps[firstPath])
      return navigate(`/setup${firstPath}`)

    if (activeStep === -1 || activeStep > firstIncompleteStepIndex) {
      const next = steps[firstIncompleteStepIndex]
      if (!next) return navigate('/settings')
      const { path } = next
      return navigate(`/setup${path}`)
    }
  }

  useEffect(() => {
    const activeIndex = paths.indexOf(pathname.split('/setup')[1])
    setActiveStep(activeIndex)
  }, [pathname])

  useEffect(() => {
    if (isLoading) return
    navigateToFirstIncompleteStep()
  }, [isLoading])

  if (isLoading || businessTypesQuery.isLoading) return null

  return (
    <WizardContextProvider
      value={{
        companyConfig,
        navigateToNext,
        navigateToPrevious,
        wizardState,
        updateWizardState,
      }}
    >
      <Box>
        <Stepper
          activeStep={activeStep}
          sx={{ mb: '32px', mt: '16px' }}
          nonLinear
        >
          {steps.map((s, i) => {
            return (
              <Step
                key={i}
                completed={
                  wizardSteps[s.path] && wizardSteps[s.path] !== 'NOT_COMPLETED'
                }
              >
                <StepButton
                  disabled={
                    (!wizardSteps[s.path] ||
                      wizardSteps[s.path] === 'NOT_COMPLETED') &&
                    i > firstIncompleteStepIndex
                  }
                  color='inherit'
                  onClick={() => navigate(`/setup${s.path}`)}
                >
                  {s.label}
                </StepButton>
                {/* <StepLabel>{s.label}</StepLabel> */}
              </Step>
            )
          })}
        </Stepper>
        <Box
          sx={{
            // backgroundColor: '#9ac4e7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {(isLoading || !companyConfig) && <CircularProgress />}
          {!isLoading && companyConfig && (
            <Outlet
              context={{
                navigateToNext,
                companyConfig,
                setCompanyConfig,
                updateCompanyConfig,
                wizardState,
                updateWizardState,
              }}
            />
          )}
        </Box>
      </Box>
    </WizardContextProvider>
  )
}

export default Wizard
